import { Spacing } from '@persona-id/design-tokens';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleAlertIcon,
} from '@persona-id/design-tokens/icons';
import { uniq } from 'lodash/fp';
import { ChangeEvent, createRef, Dispatch, SetStateAction, useRef, VFC } from 'react';
import styled from 'styled-components';

import FormError from 'dashboard/components/FormControls/FormError';
import Slider from 'dashboard/components/Slider';
import WorkflowStatusSticker from 'dashboard/components/WorkflowStatusSticker';
import { Environment } from 'dashboard/models/environment';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

interface Errors {
  environmentSelected?: string;
  rollout?: string;
}
interface WorkflowEnvironmentsConfigurationProps {
  editable: boolean;
  activeEnvironmentIds: string[];
  availableEnvironments: Environment[];
  selected: Set<string>;
  rolloutPercentage: number;
  disableSlider: boolean;
  canToggleRollout: boolean;
  rolloutToggled?: boolean;
  errors?: Errors;
  showEnvironmentsSection?: boolean;
  onSelect: (value: string) => (e: ChangeEvent<HTMLInputElement>) => void;
  onSelectAll: () => void;
  setRolloutPercentage: Dispatch<SetStateAction<number>>;
  toggleRollout?: () => void;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${Spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[2]} 0px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
`;

const StyledButton = styled(Button).attrs(() => ({ width: 'icon', variant: 'basic' }))`
  background: none;
`;

const SelectAllButton = styled(Button).attrs(() => ({
  width: 'auto',
  variant: 'basic',
}))`
  padding: 0px;
  background: none;
  height: auto;
`;

const EnvironmentAlertBanner = styled(Banner)`
  margin-top: 8px;
  padding: 8px;
  display: flex;
  flex-direction: row;
  vertical-align: middle;
`;

const EnvironmentAlertIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;
const EnvironmentAlertMessageContainer = styled.div``;

interface EnvironmentSectionProps {
  environmentId: string;
  environmentName: string;
  active: boolean;
  disabled: boolean;
  checked: boolean;
  onSelect: (value: string) => (e: ChangeEvent<HTMLInputElement>) => void;
}

const EnvironmentSection = ({
  environmentId,
  environmentName,
  active,
  disabled,
  checked,
  onSelect,
}: EnvironmentSectionProps) => {
  return (
    <Row>
      <Cell>
        <Checkbox
          id={environmentId}
          disabled={disabled}
          checked={checked}
          onChange={onSelect(environmentId)}
          label={<Paragraph color='primary'>{environmentName}</Paragraph>}
        />
      </Cell>
      <Cell>
        <WorkflowStatusSticker workflowStatus={active} />
      </Cell>
    </Row>
  );
};

const WorkflowEnvironmentsConfiguration: VFC<WorkflowEnvironmentsConfigurationProps> = ({
  editable,
  activeEnvironmentIds,
  availableEnvironments,
  selected,
  rolloutPercentage,
  disableSlider,
  canToggleRollout,
  rolloutToggled = false,
  errors = {},
  showEnvironmentsSection = true,
  onSelect,
  onSelectAll,
  setRolloutPercentage,
  toggleRollout,
}) => {
  const [isEnvironmentsSectionOpen, { toggle: toggleEnvironmentsSection }] = useToggle(true);

  const activeEnvironmentTypes = uniq(
    availableEnvironments
      .filter((environment) => activeEnvironmentIds.includes(environment.id))
      .map((environment) => environment.type),
  );
  const selectedEnvironmentTypes = availableEnvironments
    .filter((environment) => Array.from(selected).includes(environment.id))
    .map((environment) => environment.type);

  const showProductionEnvironmentWarning =
    !selectedEnvironmentTypes.includes(ApiModel.EnvironmentProduction) &&
    activeEnvironmentTypes.includes(ApiModel.EnvironmentProduction);

  const environmentRows = availableEnvironments.map((environment: Environment, idx: number) => {
    return (
      <EnvironmentSection
        key={idx}
        environmentId={environment.id}
        environmentName={environment.attributes.name}
        active={activeEnvironmentIds.includes(environment.id)}
        disabled={!editable}
        checked={selected.has(environment.id)}
        onSelect={onSelect}
      />
    );
  });
  const rolloutSectionRef = createRef<HTMLDivElement>();
  const isDraggingRef = useRef<boolean>(false);

  return (
    <section>
      {showEnvironmentsSection && (
        <>
          <Paragraph color='primary'>
            Choose the environments in which your Workflow will be active. Unselect an environment
            to make it inactive.
          </Paragraph>
          {showProductionEnvironmentWarning && (
            <EnvironmentAlertBanner variant='alert'>
              <EnvironmentAlertIconContainer>
                <CircleAlertIcon />
              </EnvironmentAlertIconContainer>
              <EnvironmentAlertMessageContainer>
                WARNING: No production environments are selected. Publishing the workflow will
                deactivate Workflows in all production environments.
              </EnvironmentAlertMessageContainer>
            </EnvironmentAlertBanner>
          )}
          <Spacer height={3} />
          <Header>
            <Cell>
              <StyledButton
                id='workflow-environments-configuration__chevron'
                prefixIcon={isEnvironmentsSectionOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                onClick={toggleEnvironmentsSection}
              />
              <Label htmlFor='workflow-environments-configuration__chevron' size='lg' weight='bold'>
                Activate environments
              </Label>
            </Cell>
            <Cell>
              <SelectAllButton
                id='workflow-environments-configuration__select-all'
                disabled={!editable}
                onClick={onSelectAll}
              >
                <Paragraph>Select all</Paragraph>
              </SelectAllButton>
            </Cell>
          </Header>
          <div>{isEnvironmentsSectionOpen && environmentRows}</div>
          {errors.environmentSelected && <FormError>{errors.environmentSelected}</FormError>}
          <Spacer height={3} />
        </>
      )}
      <Header>
        <Cell>
          {canToggleRollout && (
            <Checkbox
              id='workflow-environments-configuration__toggle-rollout'
              checked={rolloutToggled}
              onChange={toggleRollout}
              label={
                <Label
                  htmlFor='workflow-environments-configuration__toggle-rollout'
                  size='lg'
                  weight='bold'
                >
                  Stage a roll-out
                </Label>
              }
            />
          )}
          {!canToggleRollout && (
            <Label size='lg' weight='bold'>
              Manage roll-out settings
            </Label>
          )}
        </Cell>
        <Cell>
          <Paragraph>Applies to all selected environments.</Paragraph>
        </Cell>
      </Header>
      {(!canToggleRollout || rolloutToggled) && (
        <div ref={rolloutSectionRef}>
          <Slider
            isDraggingRef={isDraggingRef}
            percentage={rolloutPercentage}
            setPercentage={setRolloutPercentage}
            boundingSectionRef={rolloutSectionRef}
            leftDescriptor='This version'
            rightDescriptor='Previous version'
            disabled={disableSlider || !editable}
          />
          {errors.rollout && <FormError>{errors.rollout}</FormError>}
        </div>
      )}
    </section>
  );
};

export default WorkflowEnvironmentsConfiguration;
